/**
 * socket.ts
 * Socket.IO client for the admin panel.
 *
 * Connects to the API server with JWT auth token.
 * Lazily creates the socket on first `getSocket()` call.
 * Provides `reconnectSocket()` to re-establish with a fresh token (e.g. after login).
 */

import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/store/authStore';

/**
 * API server origin for the Socket.IO connection (the `/api` suffix is stripped
 * — Socket.IO connects to the server root, not the REST prefix).
 *
 * When `VITE_API_URL` is set (split-domain staging / production builds) we
 * connect to that absolute origin. When it is UNSET/empty (local dev) we
 * connect to an empty string, which Socket.IO treats as "same origin as the
 * page" — so the connection targets whatever host:port served the SPA (e.g. the
 * Vite dev server on :3001) and Vite proxies `/socket.io` through to the API on
 * 127.0.0.1:3000. That keeps the admin panel reachable from another machine on
 * the LAN without baking this host's IP into the bundle.
 */
const SOCKET_URL = import.meta.env.VITE_API_URL
  ? String(import.meta.env.VITE_API_URL).replace(/\/api\/?$/, '')
  : '';

let socket: Socket | null = null;

/**
 * Get or create the Socket.IO client instance.
 * Attaches the JWT accessToken from the auth store for server-side verification.
 */
export function getSocket(): Socket {
  if (!socket) {
    const { accessToken } = useAuthStore.getState();
    socket = io(SOCKET_URL, {
      auth: { token: accessToken },
      autoConnect: true,
      reconnectionDelay: 2000,
    });
  }
  return socket;
}

/**
 * Reconnect the socket with a fresh token.
 * Called after login or token refresh to ensure the server accepts the
 * connection. Drops every previously-attached event listener before the
 * reconnect so a stale handler from a different user's session can't
 * keep firing on the new connection. Consumers re-attach their listeners
 * inside their own `useEffect`, which fires again after the auth state
 * change that triggered this reconnect.
 */
export function reconnectSocket(): void {
  const { accessToken } = useAuthStore.getState();
  if (socket) {
    socket.removeAllListeners();
    socket.auth = { token: accessToken };
    socket.disconnect().connect();
  }
}

/**
 * Disconnect and destroy the socket instance.
 * Called on logout.
 */
export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}
